/**
 * Client-side image compression using canvas.
 * - Resizes to max width/height while preserving aspect ratio
 * - Re-encodes as JPEG with adjustable quality
 * - Skips compression for non-images or already-small files
 */

const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.82;
const SKIP_THRESHOLD_BYTES = 300 * 1024; // < 300KB → don't bother compressing

const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/heic"];

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

/**
 * Compress an image file. Returns a new File (jpeg) or the original if not applicable.
 */
export async function compressImage(file, opts = {}) {
  if (!file || !file.type) return file;
  if (!IMAGE_TYPES.includes(file.type)) return file;
  if (file.size <= SKIP_THRESHOLD_BYTES) return file;

  const maxDim = opts.maxDimension || MAX_DIMENSION;
  const quality = opts.quality ?? JPEG_QUALITY;

  try {
    const img = await loadImage(file);
    let { width, height } = img;
    const largest = Math.max(width, height);
    if (largest > maxDim) {
      const scale = maxDim / largest;
      width = Math.round(width * scale);
      height = Math.round(height * scale);
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await new Promise((res) =>
      canvas.toBlob(res, "image/jpeg", quality)
    );
    if (!blob) return file;

    // Only accept if smaller
    if (blob.size >= file.size) return file;

    const newName = file.name.replace(/\.(png|jpe?g|webp|heic)$/i, "") + ".jpg";
    return new File([blob], newName, {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } catch (e) {
    console.warn("Image compression failed, using original:", e);
    return file;
  }
}
